/**
 * Reads and parses template configuration for simple form
 * Determines which fields should be shown based on template settings
 */
export default class SimpleFormTemplateReader {
    /**
     * Create a new template reader
     * 
     * @param {number} templateId - Template ID to load
     */
    constructor(templateId) {
        this.templateId = templateId;
        this.templateConfig = null;
        this.simpleConfig = null;
        this.isLoaded = false;
    }
    
    /**
     * Load template configuration from server
     * 
     * @returns {Promise<Object>} Template configuration
     */
    async loadTemplate() {
        try {
            const response = await fetch('/wp-admin/admin-ajax.php', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                },
                body: new URLSearchParams({
                    action: 'load_template',
                    template_id: this.templateId,
                    nonce: window.octStcCustomizer?.nonce || ''
                })
            });

            if (!response.ok) {
                throw new Error('Failed to load template');
            }
            
            const data = await response.json();
            
            if (!data.success) {
                throw new Error(data.data || 'Failed to load template configuration');
            }

            // Store template configuration
            this.templateConfig = data.data.config ? JSON.parse(data.data.config) : {};
            this.extractSimpleConfig();
            this.isLoaded = true;
            
            return this.templateConfig;
            
        } catch (error) {
            console.error('Error loading template:', error);
            
            // Use fallback configuration from global variable
            this.useGlobalConfig();
            
            throw error;
        }
    }
    
    /**
     * Extract simple form configuration from template
     */
    extractSimpleConfig() {
        // Try to get simple config from template
        if (this.templateConfig && this.templateConfig.simple && this.templateConfig.simple.fields) {
            this.simpleConfig = this.templateConfig.simple.fields;
        } else {
            // Use global config as fallback
            this.useGlobalConfig();
        }
    }
    
    /**
     * Use global configuration as fallback
     */
    useGlobalConfig() {
        if (window.stcSimpleConfig) {
            this.simpleConfig = window.stcSimpleConfig;
            this.isLoaded = true;
        } else {
            // Final fallback - default configuration
            this.simpleConfig = {
                team_name: { enabled: true },
                team_number: { enabled: false }
            };
            this.isLoaded = true;
        }
    }
    
    /**
     * Get simple form field configuration
     * 
     * @returns {Object} Simple form configuration
     */
    getSimpleConfig() {
        if (!this.isLoaded) {
            this.useGlobalConfig();
        }
        
        return this.simpleConfig || {};
    }
    
    /**
     * Check if a field is enabled
     * 
     * @param {string} fieldName - Field name to check
     * @returns {boolean} Whether field is enabled
     */
    isFieldEnabled(fieldName) {
        const config = this.getSimpleConfig();
        return config[fieldName] && config[fieldName].enabled === true;
    }
    
    /**
     * Get all enabled fields
     * 
     * @returns {Array} Array of enabled field names
     */
    getEnabledFields() {
        const config = this.getSimpleConfig();
        const enabledFields = ['user_image', 'user_name']; // Always required
        
        // Add optional fields if enabled
        Object.keys(config).forEach(fieldName => {
            if (this.isFieldEnabled(fieldName)) {
                enabledFields.push(fieldName);
            }
        });
        
        return enabledFields;
    }
    
    /**
     * Get template information
     * 
     * @returns {Object} Template info object
     */
    getTemplateInfo() {
        return {
            id: this.templateId,
            title: this.templateConfig?.title || 'Default Template',
            dimensions: this.templateConfig?.dimensions || { width: 63, height: 88 }
        };
    }
    
    /**
     * Get field mapping for step system
     * Maps simple form fields to existing step system
     * 
     * @returns {Object} Field mapping object
     */
    getFieldMapping() {
        return {
            user_image: {
                stepId: 'image',
                side: 'front',
                type: 'image',
                required: true
            },
            user_name: {
                stepId: 'name',
                side: 'front', 
                type: 'text',
                required: true
            },
            team_name: {
                stepId: 'team_name',
                side: 'front',
                type: 'text',
                required: false,
                enabled: this.isFieldEnabled('team_name')
            },
            team_number: {
                stepId: 'number',
                side: 'front',
                type: 'text',
                required: false,
                enabled: this.isFieldEnabled('team_number')
            }
        };
    }
    
    /**
     * Check if template supports simple form
     * 
     * @returns {boolean} Whether template supports simple form
     */
    supportsSimpleForm() {
        // For now, all templates support simple form
        // This could be extended to check specific template capabilities
        return true;
    }
    
    /**
     * Get validation rules for form fields
     * 
     * @returns {Object} Validation rules
     */
    getValidationRules() {
        return {
            user_image: {
                required: true,
                type: 'file',
                accept: ['image/jpeg', 'image/jpg', 'image/png'],
                maxSize: 5 * 1024 * 1024, // 5MB
                errorMessages: {
                    required: 'Please upload your image',
                    invalidType: 'Please upload a JPG or PNG image',
                    maxSize: 'Image must be smaller than 5MB'
                }
            },
            user_name: {
                required: true,
                type: 'text',
                maxLength: 50,
                pattern: /^[a-zA-Z\s\-\.\']+$/,
                errorMessages: {
                    required: 'Please enter your name',
                    maxLength: 'Name must be less than 50 characters',
                    pattern: 'Name can only contain letters, spaces, hyphens, periods and apostrophes'
                }
            },
            team_name: {
                required: false,
                type: 'text',
                maxLength: 50,
                pattern: /^[a-zA-Z0-9\s\-\.\']+$/,
                errorMessages: {
                    maxLength: 'Team name must be less than 50 characters',
                    pattern: 'Team name can only contain letters, numbers, spaces, hyphens, periods and apostrophes'
                }
            },
            team_number: {
                required: false,
                type: 'text',
                maxLength: 10,
                pattern: /^[0-9]+$/,
                errorMessages: {
                    maxLength: 'Number must be less than 10 digits',
                    pattern: 'Please enter numbers only'
                }
            }
        };
    }
}